import {
	Coding,
	Questionnaire,
	QuestionnaireItem,
	QuestionnaireResponse,
	QuestionnaireResponseItem,
	QuestionnaireResponseItemAnswer,
} from "fhir/r4";
import _matches from "lodash/fp/matches";
import _overArgs from "lodash/fp/overArgs";
import _pick from "lodash/fp/pick";
import { Content, TableCell } from "pdfmake/interfaces";

import { extractScore } from "@intocare/assessments/lib/scoring";
import { isEnabled } from "@intocare/assessments/lib/validate/enable-when";

import { DocumentContext } from "./document-context";
import { divider } from "./generic";

type QuestionnaireContext = {
	items: Map<string, { item: QuestionnaireItem; response?: QuestionnaireResponseItem }>;
	response: QuestionnaireResponse;
};

const QUESTIONNAIRE_ITEM_RECURSION_DEPTH_LIMIT = 20; // Seems to be TypeScript's default (20).

/** Collects items for the passed-through context to easily handle things like cross-item checks. */
function collectItemsForContext(
	data: { items: QuestionnaireItem[]; responses?: QuestionnaireResponseItem[] },
	result: QuestionnaireContext["items"] = new Map(),
	recursionDepth = 1,
) {
	if (recursionDepth > QUESTIONNAIRE_ITEM_RECURSION_DEPTH_LIMIT) {
		throw new Error(`Questionnaire item recursion depth exceeded!`);
	}

	const responses = new Map(data.responses?.map((response) => [response.linkId, response]) ?? []);

	data.items.forEach((item) => {
		const response = responses.get(item.linkId);

		result.set(item.linkId, { item, response });

		if (item.item) {
			collectItemsForContext({ items: item.item, responses: response?.item }, result, recursionDepth + 1);
		}
	});

	return result;
}

const matchesValueCoding: (s: { valueCoding?: Coding }) => (o: { valueCoding?: Coding }) => boolean = _overArgs(
	_matches,
	_pick(["valueCoding.code", "valueCoding.system", "valueCoding.version"]),
);

function answerContent(item: QuestionnaireItem) {
	return (answer: QuestionnaireResponseItemAnswer): Content => {
		if (answer.valueInteger !== undefined) {
			return answer.valueInteger.toString();
		}
		if (answer.valueString !== undefined) {
			return answer.valueString;
		}
		if (answer.valueCoding) {
			return (
				(answer.valueCoding.code && item.answerOption?.find(matchesValueCoding(answer))?.valueCoding?.display)
				?? answer.valueCoding.display
				?? answer.valueCoding.code
				?? ""
			);
		}
		throw new Error(`Rendering the questionnaire item answer '${JSON.stringify(answer)}' is not implemented.`);
	};
}

function questionnaireItemContent(context: QuestionnaireContext, item: QuestionnaireItem): Content {
	switch (item.type) {
		case "group":
			return [
				{ text: [item.prefix, item.text].filter(Boolean).join(" "), bold: true },
				{ marginLeft: 12, stack: [questionnaireItemsContent(context, item.item ?? [])] },
			];
		case "display":
			return { text: item.text ?? "", fontSize: 9, color: "gray" };
		default:
			return {
				columnGap: 12,
				columns: [
					{ width: "*", text: [item.prefix, item.text].filter(Boolean).join(" ") },
					{
						width: "*",
						stack: context.items.get(item.linkId)?.response?.answer?.map(answerContent(item)) ?? ["-"],
					},
				],
			};
	}
}

function questionnaireItemsContent(context: QuestionnaireContext, items: QuestionnaireItem[]): Content {
	return items
		.filter((item: QuestionnaireItem) => isEnabled(item, context.response))
		.flatMap((item, index) =>
			index > 0
				? [divider(), questionnaireItemContent(context, item)]
				: { marginTop: 8, stack: [questionnaireItemContent(context, item)] },
		);
}

export function questionnaireContent(data: { questionnaire: Questionnaire; response: QuestionnaireResponse }): Content {
	return questionnaireItemsContent(
		{
			items: collectItemsForContext({ items: data.questionnaire.item ?? [], responses: data.response?.item }),
			response: data.response,
		},
		data.questionnaire.item ?? [],
	);
}

export function questionnaireScoringContent(
	ctx: DocumentContext,
	data: { questionnaire: Questionnaire; response?: QuestionnaireResponse },
): Content {
	const score = data.response && extractScore(data.questionnaire, data.response);
	const components: TableCell[] = score?.component?.map((component) => [
		{ alignment: "center", stack: [component.titleDisplay, { text: component.display, fontSize: 14 }] },
	]) ?? [""];

	return {
		table: {
			widths: [...components.map(() => "*"), "auto"],
			body: [
				[
					...components,
					{
						alignment: "center",
						stack: [
							ctx.upperCase(ctx.t("domain-model.submittedAssessment.property.totalScore.label")),
							{ text: score?.display ?? "-", fontSize: 14, bold: true },
						],
					},
				],
			],
		},
		layout: {
			hLineWidth: () => 0,
			vLineWidth: (i) => (i === components.length ? 1 : 0),
			vLineColor: "lightgray",
			paddingRight: (i) => (i === components.length - 1 ? 8 : 0),
			paddingLeft: (i) => (i === components.length ? 8 : 0),
		},
	};
}
